import { DARK_SHOP_TOWERS, LIGHT_SHOP_TOWERS } from './originShop';
import type { TowerType } from '../core/types';
import { CAPSULE_TOWERS } from './capybaraEvent';
import { GOLDEN_BAG_TOWERS } from './restaurant';
import { SHOP_ORDER, TOWER_UNLOCKS } from './unlocks';

/**
 * The Variety Orb — the campaign finale's prize.
 *
 * Won exactly once, by beating Level 25 (*The Ember and Flame*). It opens into
 * one random tower drawn with EQUAL probability from every tower that is not a
 * Chapter 1 starter; a tower the player already has is sold for coins instead.
 *
 * This module is the whole rulebook, and like `config/restaurant.ts` it is pure
 * — no Phaser, no storage, no clock — so the pool and the odds are
 * unit-testable and the render layer is left with nothing but presentation.
 *
 * It is deliberately NOT built on `TAKEOUT_BAG_ODDS`. The bag is a weighted
 * draw from five towers paying 75 coins on a duplicate; the orb is a flat draw
 * from fourteen paying 500. Sharing the machinery would mean parameterising
 * every part of it and gaining nothing.
 */

/**
 * The five towers a Chapter 1 episode introduces (Hambi Tower, Pickle Ranger,
 * Cheese Guardian, Bacon and Sauce). Every player has these by Level 5, so the
 * orb never rolls one.
 *
 * Derived from `TOWER_UNLOCKS` rather than retyped, so retiring or adding an
 * episode tower moves the orb's pool with it automatically.
 */
export const BASE_TOWERS: readonly TowerType[] = Object.values(TOWER_UNLOCKS).flat();

/**
 * King Hambi is the campaign finale's own prize, won by beating King Ember in
 * the SAME level the orb is paid for — so the orb must never hand him out.
 * Subtracted explicitly for the capsule towers' reason: the pool derives from
 * the roster, and he is on it.
 */
// The Golden Shroom is Pathway of the Fungi's own prize the same way.
const ORB_EXCLUDED: readonly TowerType[] = ['kinghambi', 'goldenshroom'];

/**
 * Everything the orb can contain, in the canonical shop/display order: the
 * whole roster minus the base towers, minus the Capybara Event's three.
 * Seventeen today (the Market's three included).
 *
 * The capsule's Hambis are subtracted explicitly because this list DERIVES
 * from the roster: adding them to `SHOP_ORDER` would otherwise have handed
 * event-exclusive towers out through a Level 25 reward, silently.
 *
 * The pool intentionally includes towers reachable another way — the Wizard
 * and Sensei (win rewards, so a Level 25 winner already owns both) and
 * Shrekbook (the Test Arena challenge). "Equal chance for each of the towers"
 * is the rule; those rolls simply pay out as duplicates.
 */
export const VARIETY_ORB_TOWERS: readonly TowerType[] = SHOP_ORDER.filter(
  (t) =>
    !BASE_TOWERS.includes(t) &&
    !CAPSULE_TOWERS.includes(t) &&
    // The Golden Takeout Bag's three are bag-exclusive for the same reason.
    !GOLDEN_BAG_TOWERS.includes(t) &&
    // ...and so are the Origin Event's Dark Shop three (spec 2026-10-12).
    !(DARK_SHOP_TOWERS as readonly TowerType[]).includes(t) &&
    // ...and the Light Shop three (spec 2026-10-13).
    !(LIGHT_SHOP_TOWERS as readonly TowerType[]).includes(t) &&
    !ORB_EXCLUDED.includes(t),
);

/** Coins paid when the orb rolls a tower the player already owns. */
export const VARIETY_ORB_REFUND = 500;

/**
 * Draw one tower from the orb. `rand` must return a value in [0,1) and is
 * REQUIRED — the render layer passes the real generator, the tests pass a
 * fixed sequence. It has no default on purpose: `src/config` is part of the
 * pure layer, which the purity test forbids from naming a nondeterministic
 * source.
 *
 * A flat index, not the bag's cumulative-weight walk: every entry is equally
 * likely, so there are no bands to accumulate and none of the floating-point
 * drift `rollBag` has to round away. The clamp is the same insurance the bag
 * carries — a `rand()` of exactly 1 would otherwise index past the end.
 */
export function rollVarietyOrb(rand: () => number): TowerType {
  const r = Math.min(Math.max(rand(), 0), 1 - Number.EPSILON);
  return VARIETY_ORB_TOWERS[Math.floor(r * VARIETY_ORB_TOWERS.length)];
}

/**
 * Where the orb currently is.
 *
 * `''` — never won; beating Level 25 still triggers the cutscene.
 * `'stored'` — won and unopened, sitting on the Storage shelf.
 * `'opened'` — won and opened; nothing left to do.
 *
 * One value rather than a count and a flag: there is exactly one Variety Orb
 * in the game, ever.
 */
export type OrbState = '' | 'stored' | 'opened';

/** Narrows an arbitrary stored string to an OrbState (anything else is ''). */
export function asOrbState(raw: string | null): OrbState {
  return raw === 'stored' || raw === 'opened' ? raw : '';
}
